/**
 * V159 — HMS Key Wizard.
 *
 * Admin-only page at /admin/live-classroom-setup. Lets an admin paste 100ms
 * credentials, test the connection to 100ms, auto-generate the webhook
 * secret, copy the webhook URL to paste in the 100ms dashboard, and enable
 * / disable the native classroom without touching env files.
 *
 * All secret handling stays server-side — this page only ever sees masked
 * previews of the stored values (first 3 + last 4 chars).
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Loader2, CheckCircle2, AlertTriangle, Copy, Zap, Save,
    KeyRound, Webhook, ShieldOff, Link2, ExternalLink, Send,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const STEP_LABELS = ["Get keys", "Paste keys", "Configure webhook", "Test & enable"];

export default function LiveClassroomSetup() {
    const [loading, setLoading] = useState(true);
    const [settings, setSettings] = useState(null);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [webhookTesting, setWebhookTesting] = useState(false);
    const [webhookResult, setWebhookResult] = useState(null);
    const [disabling, setDisabling] = useState(false);

    const [accessKey, setAccessKey] = useState("");
    const [appSecret, setAppSecret] = useState("");
    const [templateId, setTemplateId] = useState("");
    const [webhookSecret, setWebhookSecret] = useState("");

    const reload = useCallback(async () => {
        try {
            const r = await api.get(`/admin/hms/settings?_=${Date.now()}`);
            setSettings(r.data);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    const save = async (payload) => {
        setSaving(true);
        try {
            await api.post("/admin/hms/settings", payload);
            toast.success("Settings saved");
            setAccessKey("");
            setAppSecret("");
            setTemplateId("");
            setWebhookSecret("");
            await reload();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const saveKeys = async (e) => {
        e?.preventDefault?.();
        const payload = {};
        if (accessKey.trim()) payload.access_key = accessKey.trim();
        if (appSecret.trim()) payload.app_secret = appSecret.trim();
        if (templateId.trim()) payload.template_id = templateId.trim();
        if (webhookSecret.trim()) payload.webhook_secret = webhookSecret.trim();
        if (Object.keys(payload).length === 0) {
            toast("Nothing changed — enter a value in at least one field.", { icon: "ℹ️" });
            return;
        }
        await save(payload);
    };

    const generateWebhookSecret = async () => {
        await save({ auto_generate_webhook_secret: true });
    };

    const testConnection = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const r = await api.post("/admin/hms/test/connection");
            setTestResult({ ok: true, ...r.data });
            toast.success("Connection successful");
        } catch (e) {
            const d = e?.response?.data?.detail;
            setTestResult({ ok: false, code: d?.code, message: d?.message || formatApiError(d) });
            toast.error(d?.message || "Connection failed");
        } finally {
            setTesting(false);
        }
    };

    const fireWebhookTest = async () => {
        setWebhookTesting(true);
        setWebhookResult(null);
        try {
            const r = await api.post("/admin/hms/test/webhook");
            setWebhookResult(r.data);
            if (r.data.ok) toast.success("Webhook round-trip verified");
            else toast.error(r.data.message || "Webhook test failed");
        } catch (e) {
            setWebhookResult({ ok: false, message: formatApiError(e?.response?.data?.detail) || "Webhook test failed" });
            toast.error("Webhook test failed");
        } finally {
            setWebhookTesting(false);
        }
    };

    const disable = async () => {
        if (!window.confirm("Disable the native classroom for everyone? Existing rooms will not be interrupted."))
            return;
        setDisabling(true);
        try {
            await api.post("/admin/hms/settings/disable");
            toast.success("Native classroom disabled");
            setTestResult(null);
            await reload();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        } finally {
            setDisabling(false);
        }
    };

    const copyToClipboard = async (text, label) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(`${label} copied`);
        } catch (_e) {
            toast.error("Copy failed");
        }
    };

    if (loading) return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
        </div>
    );

    const s = settings || {};
    const isConfigured = !!s.enabled;

    return (
        <div className="min-h-screen bg-slate-50 classroom-mobile" data-testid="hms-wizard-page">
            <header className="bg-white border-b border-slate-200">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <Link to="/admin/dashboard" className="p-2 -ml-2 rounded hover:bg-slate-100 text-slate-600" data-testid="hms-wizard-back">
                            <ArrowLeft className="w-5 h-5" />
                        </Link>
                        <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-widest text-cyan-600 font-bold">Live Classroom · Setup</div>
                            <h1 className="text-lg sm:text-xl font-heading font-bold text-[#0F172A]">Turn on Native Live Classes</h1>
                            <p className="text-xs text-slate-500 mt-0.5">Paste 100ms credentials, test the webhook, flip on the native classroom for every parent + tutor.</p>
                        </div>
                    </div>
                    <StatusChip enabled={isConfigured} />
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 py-5 space-y-5">
                {/* V167 — Prominent fallback banner. Admins who don't want to
                    set up 100ms can start conducting classes today using any
                    external meeting URL. */}
                <div className="bg-gradient-to-r from-emerald-50 to-cyan-50 border-2 border-emerald-300 rounded-2xl p-4 sm:p-5" data-testid="hms-wizard-fallback-banner">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0">
                            <CheckCircle2 className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">You don't need to set this up to run classes</div>
                            <div className="text-base sm:text-lg font-heading font-bold text-[#0F172A] mt-0.5">Start conducting demo classes right now — no 100ms account required.</div>
                            <p className="text-sm text-slate-700 mt-1.5">
                                Open any assignment → <strong>Sessions</strong> tab → <strong>Add class</strong> → paste a Zoom, Google Meet, Microsoft Teams, or Jitsi URL. Parents and tutors will see a Join Class button in their portal that opens the meeting directly. All attendance, homework, materials, and reports still work.
                            </p>
                            <p className="text-xs text-slate-600 mt-2">
                                Set up 100ms below <strong>only</strong> if you want video + whiteboard to happen inside the Anand Tutorials portal (parents & tutors never open a third-party app). It's optional.
                            </p>
                            <Link
                                to="/admin/dashboard"
                                data-testid="hms-wizard-jump-to-assignments"
                                data-cm-primary
                                className="mt-3 inline-flex items-center gap-1.5 bg-emerald-600 text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-emerald-700"
                            >
                                Go to Classroom & Assignments
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Step tracker */}
                <StepTracker settings={s} testResult={testResult} />

                {/* Step 1 — Get keys */}
                <Section
                    step="1"
                    icon={ExternalLink}
                    title="Get your keys from 100ms"
                    subtitle="Free to sign up. You'll need App Access Key, App Secret, and a Template ID."
                >
                    <ol className="text-sm text-slate-700 space-y-2 list-decimal pl-5">
                        <li>
                            Sign up / log in at{" "}
                            <a href="https://dashboard.100ms.live/" target="_blank" rel="noreferrer" className="text-cyan-700 font-semibold hover:underline">dashboard.100ms.live</a>.
                        </li>
                        <li><strong>Developer → App Credentials</strong> — copy the <strong>App Access Key</strong> and <strong>App Secret</strong>.</li>
                        <li><strong>Templates → New Template</strong> — name it <code className="text-xs bg-slate-100 rounded px-1 py-0.5">anand-tutorials-classroom</code>. Add two roles: <code className="text-xs bg-slate-100 rounded px-1 py-0.5">tutor</code> (allow End Room) and <code className="text-xs bg-slate-100 rounded px-1 py-0.5">parent</code>. Both roles get publish + subscribe audio/video.</li>
                        <li>After saving the template, copy its <strong>Template ID</strong>.</li>
                    </ol>
                </Section>

                {/* Step 2 — Paste keys */}
                <Section step="2" icon={KeyRound} title="Paste your credentials" subtitle="Values are stored server-side. This page only ever shows a masked preview.">
                    <form onSubmit={saveKeys} className="space-y-3">
                        <Field
                            label="App Access Key"
                            hint={s.has_access_key ? `Currently saved: ${s.access_key_masked}` : "Not set"}
                            saved={s.has_access_key}
                        >
                            <input
                                type="text"
                                value={accessKey}
                                onChange={(e) => setAccessKey(e.target.value)}
                                placeholder={s.has_access_key ? "Leave blank to keep current" : "abcd1234-…"}
                                data-testid="hms-input-access-key"
                                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none font-mono"
                            />
                        </Field>
                        <Field
                            label="App Secret"
                            hint={s.has_app_secret ? "Currently saved · hidden" : "Not set"}
                            saved={s.has_app_secret}
                        >
                            <input
                                type="password"
                                value={appSecret}
                                onChange={(e) => setAppSecret(e.target.value)}
                                placeholder={s.has_app_secret ? "Leave blank to keep current" : "Paste secret"}
                                data-testid="hms-input-app-secret"
                                autoComplete="new-password"
                                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none font-mono"
                            />
                        </Field>
                        <Field
                            label="Template ID"
                            hint={s.has_template_id ? `Currently saved: ${s.template_id_masked}` : "Not set"}
                            saved={s.has_template_id}
                        >
                            <input
                                type="text"
                                value={templateId}
                                onChange={(e) => setTemplateId(e.target.value)}
                                placeholder={s.has_template_id ? "Leave blank to keep current" : "6xxxx…"}
                                data-testid="hms-input-template-id"
                                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none font-mono"
                            />
                        </Field>
                        <div className="pt-1">
                            <button
                                type="submit"
                                disabled={saving}
                                data-testid="hms-save-keys"
                                className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-[#1E293B] disabled:opacity-60"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                Save keys
                            </button>
                        </div>
                    </form>
                </Section>

                {/* Step 3 — Webhook */}
                <Section step="3" icon={Webhook} title="Configure the 100ms webhook" subtitle="This lets us record real attendance + elapsed time. Attendance never gets calculated in the browser.">
                    <div className="space-y-3">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1">Webhook URL</div>
                            <div className="flex gap-2">
                                <input
                                    readOnly
                                    value={s.webhook_url || ""}
                                    data-testid="hms-webhook-url"
                                    className="flex-1 text-xs sm:text-sm border border-slate-200 rounded-lg px-3 py-2 bg-slate-50 font-mono"
                                />
                                <button
                                    onClick={() => copyToClipboard(s.webhook_url, "Webhook URL")}
                                    data-testid="hms-copy-webhook-url"
                                    className="inline-flex items-center gap-1 border border-slate-200 rounded-lg px-3 py-2 text-xs font-semibold hover:bg-slate-50"
                                >
                                    <Copy className="w-3.5 h-3.5" /> Copy
                                </button>
                            </div>
                            <p className="text-[11px] text-slate-500 mt-1">In the 100ms dashboard → <strong>Developer → Webhooks</strong> → paste this URL and subscribe to <code className="text-[10px] bg-slate-100 px-1 rounded">peer.join.success</code>, <code className="text-[10px] bg-slate-100 px-1 rounded">peer.leave.success</code>, <code className="text-[10px] bg-slate-100 px-1 rounded">session.close.success</code>, <code className="text-[10px] bg-slate-100 px-1 rounded">room.end.success</code>.</p>
                        </div>

                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1">Webhook Secret</div>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <input
                                    type="text"
                                    value={webhookSecret}
                                    onChange={(e) => setWebhookSecret(e.target.value)}
                                    placeholder={s.has_webhook_secret ? `Currently: ${s.webhook_secret_masked}` : "Paste from 100ms, or auto-generate"}
                                    data-testid="hms-input-webhook-secret"
                                    className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none font-mono"
                                />
                                <button
                                    type="button"
                                    onClick={generateWebhookSecret}
                                    disabled={saving}
                                    data-testid="hms-gen-webhook-secret"
                                    className="inline-flex items-center justify-center gap-1.5 bg-cyan-600 text-white text-xs font-semibold px-3 py-2 rounded-lg hover:bg-cyan-700 disabled:opacity-60"
                                >
                                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                                    Auto-generate
                                </button>
                            </div>
                            <p className="text-[11px] text-slate-500 mt-1">Paste the same secret into 100ms webhook config as a <code className="text-[10px] bg-slate-100 px-1 rounded">X-Webhook-Secret</code> custom header. Without this, we can't verify webhook authenticity and events will be rejected.</p>
                        </div>

                        {/* V159.1 — Fire test event */}
                        <div className="border-t border-slate-100 pt-3 mt-2">
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="min-w-0">
                                    <div className="text-sm font-semibold text-[#0F172A]">Fire test event</div>
                                    <p className="text-[11px] text-slate-500 mt-0.5">Sends a synthetic <code className="text-[10px] bg-slate-100 px-1 rounded">peer.join.success</code> to our own webhook, then verifies it was authenticated and recorded. Doesn't touch any real class or attendance data.</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={fireWebhookTest}
                                    disabled={webhookTesting}
                                    data-testid="hms-fire-webhook-test"
                                    className="inline-flex items-center gap-1.5 bg-white border border-cyan-300 text-cyan-800 text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-cyan-50 disabled:opacity-60 shrink-0"
                                >
                                    {webhookTesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                                    Fire test event
                                </button>
                            </div>
                            {webhookResult && <WebhookTestResult result={webhookResult} />}
                        </div>
                    </div>
                </Section>

                {/* Step 4 — Test + enable */}
                <Section step="4" icon={Link2} title="Test the connection" subtitle="Verifies your Access Key + App Secret + Template ID by talking to the 100ms API.">
                    <div className="space-y-3">
                        <button
                            onClick={testConnection}
                            disabled={testing || !s.has_access_key || !s.has_app_secret}
                            data-testid="hms-test-connection"
                            className="inline-flex items-center gap-1.5 bg-emerald-600 text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-60"
                        >
                            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                            Run connection test
                        </button>
                        {testResult && (
                            <TestResult result={testResult} />
                        )}
                    </div>
                </Section>

                {/* Disable */}
                {isConfigured && (
                    <div className="bg-white border border-rose-200 rounded-2xl p-4">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <div className="text-sm font-semibold text-rose-800">Turn off Native Classroom</div>
                                <p className="text-xs text-slate-600 mt-1">Clears the saved credentials. Portal Join Class buttons will fall back to the "not activated" state. Existing rooms won't be interrupted.</p>
                            </div>
                            <button
                                onClick={disable}
                                disabled={disabling}
                                data-testid="hms-disable"
                                className="inline-flex items-center gap-1.5 bg-white border border-rose-300 text-rose-700 text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-rose-50 disabled:opacity-60 shrink-0"
                            >
                                {disabling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldOff className="w-3.5 h-3.5" />}
                                Disable
                            </button>
                        </div>
                    </div>
                )}

                {s.updated_at && (
                    <p className="text-[11px] text-slate-500 text-center">
                        Last updated {new Date(s.updated_at).toLocaleString()} by {s.updated_by || "admin"}
                    </p>
                )}
            </main>
        </div>
    );
}

// ---------------------------------------------------------- Sub-components

function StatusChip({ enabled }) {
    return enabled ? (
        <span data-testid="hms-status-enabled" className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold px-3 py-1.5 rounded-full">
            <CheckCircle2 className="w-3.5 h-3.5" /> Live · Active
        </span>
    ) : (
        <span data-testid="hms-status-disabled" className="inline-flex items-center gap-1.5 bg-slate-100 border border-slate-200 text-slate-600 text-xs font-semibold px-3 py-1.5 rounded-full">
            <AlertTriangle className="w-3.5 h-3.5" /> Not activated
        </span>
    );
}

function StepTracker({ settings, testResult }) {
    const keysDone = settings.has_access_key && settings.has_app_secret && settings.has_template_id;
    const webhookDone = settings.has_webhook_secret;
    const testDone = testResult?.ok && testResult?.template_valid;
    const done = [true, keysDone, webhookDone, testDone && settings.enabled];
    return (
        <div className="grid grid-cols-4 gap-2 bg-white border border-slate-200 rounded-2xl p-3">
            {STEP_LABELS.map((label, i) => (
                <div key={label} className="flex flex-col items-center gap-1 text-center">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold ${
                        done[i] ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-500"
                    }`}>
                        {done[i] ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
                </div>
            ))}
        </div>
    );
}

function Section({ step, icon: Icon, title, subtitle, children }) {
    return (
        <section className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5">
            <div className="flex items-start gap-3 mb-3">
                <div className="w-8 h-8 rounded-full bg-cyan-50 border border-cyan-200 text-cyan-800 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Step {step}</div>
                    <h2 className="text-base sm:text-lg font-heading font-bold text-[#0F172A]">{title}</h2>
                    {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
                </div>
            </div>
            {children}
        </section>
    );
}

function Field({ label, hint, saved, children }) {
    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{label}</div>
                <div className={`text-[10px] ${saved ? "text-emerald-700" : "text-slate-400"}`}>{hint}</div>
            </div>
            {children}
        </div>
    );
}

function TestResult({ result }) {
    if (result.ok) {
        const good = result.template_valid;
        return (
            <div className={`border rounded-lg p-3 text-xs ${good ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`} data-testid="hms-test-result">
                <div className="font-semibold flex items-center gap-1">
                    {good ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                    {good ? "All good — Live Classroom is ready" : "Credentials work, template needs a second look"}
                </div>
                <p className="mt-1">{result.message}</p>
                {result.template_error && (
                    <pre className="mt-1.5 text-[10px] bg-white/60 rounded p-2 overflow-x-auto">{result.template_error}</pre>
                )}
            </div>
        );
    }
    return (
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-800" data-testid="hms-test-result">
            <div className="font-semibold flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Connection failed</div>
            <p className="mt-1">{result.message || "Unknown error."}</p>
            {result.code && <p className="mt-1 opacity-75">Code: <code className="text-[10px] bg-white/60 rounded px-1">{result.code}</code></p>}
        </div>
    );
}

function WebhookTestResult({ result }) {
    const tone = result.ok
        ? "bg-emerald-50 border-emerald-200 text-emerald-800"
        : "bg-rose-50 border-rose-200 text-rose-800";
    return (
        <div className={`mt-3 border rounded-lg p-3 text-xs ${tone}`} data-testid="hms-webhook-test-result">
            <div className="font-semibold flex items-center gap-1">
                {result.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                {result.ok ? "Webhook round-trip verified" : "Webhook test failed"}
            </div>
            <p className="mt-1">{result.message}</p>
            {(result.positive || result.recorded_event || result.negative) && (
                <ul className="mt-2 space-y-0.5 text-[11px] opacity-90">
                    {result.positive && (
                        <li>
                            <span className="font-semibold">Positive:</span>{" "}
                            HTTP {result.positive.status ?? "—"}
                            {result.positive.took_ms ? ` · ${result.positive.took_ms} ms` : ""}
                        </li>
                    )}
                    {result.recorded_event && (
                        <li>
                            <span className="font-semibold">Recorded:</span>{" "}
                            <code className="text-[10px] bg-white/60 rounded px-1">{result.recorded_event.event_id}</code>
                        </li>
                    )}
                    {result.negative && !result.negative.skipped && (
                        <li>
                            <span className="font-semibold">Wrong-secret rejection:</span>{" "}
                            HTTP {result.negative.status ?? "—"} {result.negative.ok ? "✓" : "✗"}
                        </li>
                    )}
                </ul>
            )}
        </div>
    );
}
